/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { entitySwaggerToEntityStore } from 'src/luis/core/services/entities/mappers';
import { ListEntity } from 'src/luis/projects/entities/types/ListEntity';
import { ListEntitySwagger } from 'src/luis/projects/entities/types/swagger/ListEntitySwagger';
import { LuisId } from 'src/luis/types/Resource';
import { ListEntityStore } from 'src/luis/types/stores/entities/ListEntityStore';
import { SublistStore } from 'src/luis/types/stores/entities/SublistStore';

/**
 * Represents a service to perform CRUD operations for list entity.
 */
export const getListEntityService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url builder for this service.
     *
     * @param entityId An optional entity id to add.
     * @returns A url string to call.
     */
    const getUrlBuilder = (entityId: LuisId = '') =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('closedLists', entityId);

    /**
     * Gets the list entity with provided id.
     */
    const getEntity = async ({ id }: Pick<ListEntityStore, 'id'>): Promise<ListEntityStore> => {
        const requestUrl = getUrlBuilder(id).build();
        const { data: entity } = await httpService.get<ListEntitySwagger>(requestUrl, apiOptions);

        return <ListEntityStore>entitySwaggerToEntityStore(entity);
    };

    /**
     * Creates a new list entity.
     *
     * @param name The new list entity name.
     *
     * @returns A new list entity store.
     */
    const createEntity = async ({ name, sublists }: Pick<ListEntityStore, 'name' | 'sublists'>): Promise<ListEntityStore> => {
        const requestUrl = getUrlBuilder().build();
        const { data: entityId } = await httpService.post<string>(
            requestUrl,
            { name, sublists: sublists.map(sl => ({ canonicalForm: sl.name, list: sl.values })) },
            apiOptions
        );

        return getEntity({ id: entityId });
    };

    /**
     * Deletes the given list entity.
     *
     * @param id The id of the list entity to delete.
     */
    const deleteEntity = async ({ id }: Pick<ListEntityStore, 'id'>): Promise<void> => {
        const requestUrl = getUrlBuilder(id).build();
        await httpService.delete(requestUrl, apiOptions);
    };

    /**
     * Updates the given list entity.
     *
     * @param id The id of the entity to update.
     * @param name Optional: The list entity new name.
     * @param sublists Optional: The list entity new sublists.
     */
    const updateEntity = async ({
        id,
        name,
        sublists
    }: Partial<Pick<ListEntity, 'id' | 'name' | 'sublists'>>): Promise<ListEntitySwagger> => {
        const requestUrl = getUrlBuilder(id).build();

        const { data: entity } = await httpService.put<ListEntitySwagger>(
            requestUrl,
            { name, sublists: sublists.map(sl => ({ canonicalForm: sl.name, list: sl.values })) },
            apiOptions
        );

        return entity;
    };

    /**
     * Adds a new sublist to the given list entity.
     *
     * @param id The id of the list entity to add sublist to.
     * @param sublist The sublist to be added.
     */
    const addSublist = async ({
        id,
        sublist
    }: Pick<ListEntityStore, 'id'> & { sublist: Pick<SublistStore, 'name' | 'values'> }): Promise<LuisId> => {
        const requestUrl = getUrlBuilder(id)
            .resource('sublists')
            .build();

        const { data: sublistId } = await httpService.post<LuisId>(
            requestUrl,
            { canonicalForm: sublist.name, list: sublist.values },
            apiOptions
        );

        return sublistId;
    };

    /**
     * Updates an existing sublist in a given list entity.
     *
     * @param id The id of the list entity to add sublist to.
     * @param sublist The new sublist.
     */
    const updateSublist = async ({
        id,
        sublist
    }: Pick<ListEntityStore, 'id'> & {
        sublist: Partial<Pick<SublistStore, 'name' | 'values'>> & Pick<SublistStore, 'id'>;
    }): Promise<void> => {
        const requestUrl = getUrlBuilder(id)
            .resource('sublists', `${sublist.id}`)
            .build();
        await httpService.put(requestUrl, { canonicalForm: sublist.name, list: sublist.values }, apiOptions);
    };

    /**
     * Deletes an existing sublist from a given list entity.
     *
     * @param id The id of the list entity to add sublist to.
     * @param sublistId The sublist id to be deleted.
     */
    const deleteSublist = async ({ id, sublist }: Pick<ListEntityStore, 'id'> & { sublist: Pick<SublistStore, 'id'> }): Promise<void> => {
        const requestUrl = getUrlBuilder(id)
            .resource('sublists', `${sublist.id}`)
            .build();
        await httpService.delete(requestUrl, apiOptions);
    };

    return { createEntity, getEntity, updateEntity, deleteEntity, addSublist, updateSublist, deleteSublist };
});
